import { createRoot } from "react-dom/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import logo from "./logo.svg";

type Vacation = {
  id: number;
  person: string;
  start_date: string;
  end_date: string;
  note: string;
  source: string;
};

// status 0 = hálózati hiba (szerver nem elérhető)
async function api(path: string, opts?: RequestInit): Promise<{ status: number; data: any }> {
  let res: Response;
  try {
    res = await fetch(path, {
      headers: { "Content-Type": "application/json" },
      ...opts,
    });
  } catch {
    return { status: 0, data: null };
  }
  let data: any = null;
  try {
    data = await res.json();
  } catch {}
  return { status: res.status, data };
}

function errText(status: number, data: any): string {
  return data?.error ?? (status === 0 ? "Hálózati hiba — a szerver nem elérhető." : "Hiba történt.");
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Név -> stabil szín (hash -> hue), sötét témához hangolva
function personColor(name: string): { bg: string; fg: string } {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return { bg: `hsl(${hue}, 45%, 28%)`, fg: `hsl(${hue}, 85%, 83%)` };
}

const WEEKDAYS = ["H", "K", "Sze", "Cs", "P"];

function isWeekend(d: Date): boolean {
  return d.getDay() === 0 || d.getDay() === 6;
}

function Login({ onLogin }: { onLogin: () => void }) {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const { status, data } = await api("/api/login", {
      method: "POST",
      body: JSON.stringify({ user, password }),
    });
    setBusy(false);
    if (status === 200) onLogin();
    else setError(errText(status, data));
  }

  return (
    <div className="login-wrap">
      <form className="login-box" onSubmit={submit}>
        <img className="logo" src={logo} alt="AITIA" />
        <h1>AITIA Szabadság</h1>
        <input
          type="text"
          placeholder="Felhasználónév"
          value={user}
          onChange={(e) => setUser(e.target.value)}
          autoFocus
          required
        />
        <input
          type="password"
          placeholder="Jelszó"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <p className="error">{error}</p>}
        <button disabled={busy}>{busy ? "..." : "Belépés"}</button>
      </form>
    </div>
  );
}

function App() {
  const [authed, setAuthed] = useState<boolean | undefined>(undefined); // undefined = töltés
  const [view, setView] = useState<"month" | "week" | "day">("month");
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [vacations, setVacations] = useState<Vacation[]>([]);
  const [people, setPeople] = useState<string[]>([]);
  const [person, setPerson] = useState(() => localStorage.getItem("szabi_nev") ?? "");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [sel, setSel] = useState<{ a: string; b: string } | null>(null); // húzásos kijelölés
  const draggingRef = useRef(false);
  const vacReqSeq = useRef(0); // hónapváltási race ellen: csak a legutolsó kérés válasza számít

  // Naptárrács nézet szerint, hétvégék nélkül: hónap = 6x5 cella, hét = 5, nap = 1.
  const grid = useMemo(() => {
    if (view === "day") {
      const d = new Date(cursor);
      while (isWeekend(d)) d.setDate(d.getDate() + 1); // hétvégéről a következő hétfőre
      return [d];
    }
    if (view === "week") {
      const monday = new Date(cursor);
      monday.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
      return Array.from({ length: 5 }, (_, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return d;
      });
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const gridStart = new Date(first);
    gridStart.setDate(1 - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    }).filter((d) => !isWeekend(d));
  }, [cursor, view]);

  const loadVacations = useCallback(async () => {
    const seq = ++vacReqSeq.current;
    const from = iso(grid[0]);
    const to = iso(grid[grid.length - 1]);
    const { status, data } = await api(`/api/vacations?from=${from}&to=${to}`);
    if (seq !== vacReqSeq.current) return; // közben új kérés indult, ez már elavult
    if (status === 200) setVacations(data);
    else if (status === 401) setAuthed(false);
  }, [grid]);

  const loadPeople = useCallback(async () => {
    const { status, data } = await api("/api/people");
    if (status === 200) setPeople(data);
  }, []);

  useEffect(() => {
    api("/api/me").then(({ status }) => setAuthed(status === 200));
  }, []);

  useEffect(() => {
    if (authed) {
      loadVacations();
      loadPeople();
    }
  }, [authed, loadVacations, loadPeople]);

  async function addVacation(e: React.FormEvent) {
    e.preventDefault();
    if (addBusy) return;
    setAddBusy(true);
    setFormError("");
    const { status, data } = await api("/api/vacations", {
      method: "POST",
      body: JSON.stringify({ person, start, end: end || start, note }),
    });
    setAddBusy(false);
    if (status === 201) {
      localStorage.setItem("szabi_nev", person);
      setStart("");
      setEnd("");
      setNote("");
      setShowAdd(false);
      setSel(null);
      loadVacations();
      loadPeople();
    } else if (status === 401) setAuthed(false);
    else setFormError(errText(status, data));
  }

  // Kijelölés: mousedown kezdi, húzás bővíti, mouseup nyitja a popupot a tartománnyal
  function dayDown(dIso: string) {
    draggingRef.current = true;
    setSel({ a: dIso, b: dIso });
  }
  function dayEnter(dIso: string) {
    if (draggingRef.current) setSel((s) => (s ? { a: s.a, b: dIso } : s));
  }
  useEffect(() => {
    function up() {
      if (!draggingRef.current || !sel) return;
      draggingRef.current = false;
      const [lo, hi] = sel.a <= sel.b ? [sel.a, sel.b] : [sel.b, sel.a];
      setStart(lo);
      setEnd(hi);
      setNote("");
      setFormError("");
      setShowAdd(true);
    }
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [sel]);

  function closeAdd() {
    setShowAdd(false);
    setSel(null);
  }

  async function deleteVacation(id: number) {
    await api(`/api/vacations/${id}`, { method: "DELETE" });
    loadVacations();
  }

  async function logout() {
    await api("/api/logout", { method: "POST" });
    setAuthed(false);
  }

  if (authed === undefined) return null;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  function navigate(dir: number) {
    const d = new Date(cursor);
    if (view === "month") {
      d.setDate(1);
      d.setMonth(d.getMonth() + dir);
    } else if (view === "week") {
      d.setDate(d.getDate() + dir * 7);
    } else {
      d.setDate(d.getDate() + dir);
      while (isWeekend(d)) d.setDate(d.getDate() + dir); // hétvége átugrása
    }
    setCursor(d);
  }

  const todayIso = iso(new Date());
  const label =
    view === "month"
      ? cursor.toLocaleDateString("hu-HU", { year: "numeric", month: "long" })
      : view === "week"
        ? `${grid[0].toLocaleDateString("hu-HU", { year: "numeric", month: "short", day: "numeric" })} – ${grid[grid.length - 1].toLocaleDateString("hu-HU", { month: "short", day: "numeric" })}`
        : cursor.toLocaleDateString("hu-HU", { year: "numeric", month: "long", day: "numeric", weekday: "long" });
  const listFrom = view === "month" ? iso(new Date(cursor.getFullYear(), cursor.getMonth(), 1)) : iso(grid[0]);
  const listTo = view === "month" ? iso(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0)) : iso(grid[grid.length - 1]);
  const listVacs = vacations.filter((v) => v.start_date <= listTo && v.end_date >= listFrom);

  return (
    <div className="app">
      <header>
        <h1>
          <img src={logo} alt="" />
          AITIA Szabadság
        </h1>
        <button className="link" onClick={logout}>
          Kilépés
        </button>
      </header>

      <div className="main">
        <section className="cal-panel panel">
          <div className="cal-nav">
            <button className="nav-btn" aria-label="Előző" onClick={() => navigate(-1)}>
              &larr;
            </button>
            <h2>{label}</h2>
            <button className="nav-btn" aria-label="Következő" onClick={() => navigate(1)}>
              &rarr;
            </button>
            <button
              className="link"
              onClick={() => {
                const now = new Date();
                setCursor(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
              }}
            >
              Ma
            </button>
            <div className="view-switch">
              {(["month", "week", "day"] as const).map((v) => (
                <button key={v} className={view === v ? "active" : ""} onClick={() => setView(v)}>
                  {v === "month" ? "Hónap" : v === "week" ? "Hét" : "Nap"}
                </button>
              ))}
            </div>
          </div>

          <div className={`calendar view-${view}`}>
            {view !== "day" &&
              WEEKDAYS.map((d) => (
                <div key={d} className="weekday">
                  {d}
                </div>
              ))}
            {grid.map((d) => {
              const dIso = iso(d);
              const inMonth = view !== "month" || d.getMonth() === cursor.getMonth();
              const dayVacs = vacations.filter((v) => v.start_date <= dIso && v.end_date >= dIso);
              const selLo = sel ? (sel.a <= sel.b ? sel.a : sel.b) : null;
              const selHi = sel ? (sel.a <= sel.b ? sel.b : sel.a) : null;
              const inSel = selLo !== null && dIso >= selLo && dIso <= selHi!;
              return (
                <div
                  key={dIso}
                  className={
                    "day" + (inMonth ? "" : " dim") + (dIso === todayIso ? " today" : "") + (inSel ? " sel" : "")
                  }
                  onMouseDown={() => dayDown(dIso)}
                  onMouseEnter={() => dayEnter(dIso)}
                  title="Kattints vagy húzz szabadság beírásához"
                >
                  {view !== "day" && <span className="num">{d.getDate()}</span>}
                  <div className="chips">
                    {dayVacs.map((v) => {
                      const c = personColor(v.person);
                      return (
                        <span
                          key={v.id}
                          className="chip"
                          style={{ background: c.bg, color: c.fg }}
                          title={`${v.person}: ${v.start_date} – ${v.end_date}${v.note ? " (" + v.note + ")" : ""}`}
                        >
                          <span className="chip-name">{v.person}</span>
                          {v.note && <span className="chip-note">{v.note}</span>}
                        </span>
                      );
                    })}
                    {view === "day" && dayVacs.length === 0 && (
                      <p className="hint day-empty">Ezen a napon senki nincs szabadságon.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="side">
          <div className="vac-list panel">
        <h3>Szabadságok — {label}</h3>
        {listVacs.length === 0 && <p className="hint">Nincs bejegyzés ebben az időszakban.</p>}
        <ul>
          {listVacs.map((v) => {
            const c = personColor(v.person);
            return (
              <li key={v.id}>
                <span className="chip" style={{ background: c.bg, color: c.fg }}>
                  {v.person}
                </span>
                <span>
                  {v.start_date} – {v.end_date}
                </span>
                {v.note && <span className="hint">{v.note}</span>}
                <button className="link danger" onClick={() => deleteVacation(v.id)}>
                  Törlés
                </button>
              </li>
            );
          })}
        </ul>
          </div>
        </aside>
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={closeAdd}>
          <div className="modal panel" onClick={(e) => e.stopPropagation()}>
            <form className="add-form" onSubmit={addVacation}>
              <h3>Új szabadság</h3>
              <label>
                Név
                <input
                  type="text"
                  list="people"
                  value={person}
                  maxLength={60}
                  placeholder="Ki megy szabira?"
                  onChange={(e) => setPerson(e.target.value)}
                  autoFocus
                  required
                />
                <datalist id="people">
                  {people.map((p) => (
                    <option key={p} value={p} />
                  ))}
                </datalist>
              </label>
              <label>
                Mettől
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
              </label>
              <label>
                Meddig
                <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />
              </label>
              <label>
                Megjegyzés
                <input
                  type="text"
                  value={note}
                  maxLength={200}
                  placeholder="(opcionális)"
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              {formError && <span className="error">{formError}</span>}
              <div className="modal-actions">
                <button type="button" className="link" onClick={closeAdd}>
                  Mégse
                </button>
                <button disabled={addBusy}>{addBusy ? "..." : "Beírás"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
